import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, X, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';
import { FILTER_CITIES, FILTER_OCCASIONS, FILTER_PRODUCT_KITS, FILTER_TIERS } from '../lib/constants';
import { ikImage, ikThumb, ikVideo } from '../lib/imagekit';
import ImageKitUploader from './ImageKitUploader';

const TECH_OPTS = ['HD', 'Airbrush'];

const EMPTY = {
  name: '', city: 'Delhi', slug: '',
  verified: true, featured: false, published: true,
  tier: 'rising', product_kit: 'mixed',
  followers: 0, glam_score: 4.5, reviews_count: 0, starting_price: 0,
  years_experience: '', certifications: [],
  occasions: [], techniques: [],
  hairstyling: true, dupatta_draping: true, saree_draping: true,
  home_service: true, studio_service: true, trial_available: true,
  instagram: '', bio: '',
  profile_image: '', cover_image: '', featured_image: '',
  portfolio: [], reels: [],
  studio_location: '',
  category_prices: { bridal: null, engagement: null, party: null, sfx: null, airbrush: null },
};

export default function AdminArtistEdit({ mode = 'create' }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(mode === 'edit');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && slug) {
      setLoading(true);
      adminApi.list()
        .then(arr => {
          const found = arr.find(a => a.slug === slug);
          if (!found) { toast.error('Artist not found'); nav('/admin'); return; }
          setForm({
            ...EMPTY,
            ...found,
            // Drop stale picsum placeholder so the Featured picker shows "None"
            // and a Save permanently clears it.
            featured_image: (found.featured_image || '').includes('picsum.photos') ? '' : (found.featured_image || ''),
            category_prices: { ...EMPTY.category_prices, ...(found.category_prices || {}) },
            certifications: found.certifications || [],
            years_experience: found.years_experience ?? '',
          });
        })
        .catch(() => { toast.error('Could not load'); nav('/admin'); })
        .finally(() => setLoading(false));
    }
  }, [mode, slug, nav]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleIn = (k, v) => setForm(f => ({ ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] }));
  const setPrice = (cat, val) => setForm(f => ({ ...f, category_prices: { ...f.category_prices, [cat]: val === '' ? null : Number(val) } }));
  const addToList = (k, val) => { if (val && typeof val === 'string' && val.trim()) setForm(f => ({ ...f, [k]: [...f[k], val.trim()] })); };
  const removeFromList = (k, idx) => setForm(f => {
    const removed = f[k][idx];
    const next = { ...f, [k]: f[k].filter((_, i) => i !== idx) };
    // If we're removing a portfolio item that's currently selected as the
    // featured hero image, clear featured_image so we don't end up with a
    // dangling URL pointing to a deleted file.
    if (k === 'portfolio' && removed && f.featured_image === removed) {
      next.featured_image = '';
    }
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.profile_image || !form.cover_image || !form.starting_price) {
      toast.error('Name, City, Profile Image, Cover Image, and Starting Price are required.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        starting_price: Number(form.starting_price),
        followers: Number(form.followers) || 0,
        glam_score: Number(form.glam_score) || 4.5,
        reviews_count: Number(form.reviews_count) || 0,
        years_experience: form.years_experience === '' ? null : Number(form.years_experience),
        specializations: form.occasions, // mirror occasions for backward compat
        products_used: form.product_kit === 'luxury' ? ['High-End'] : form.product_kit === 'affordable' ? ['Drugstore'] : ['High-End', 'Drugstore'],
      };
      if (mode === 'edit') {
        await adminApi.update(slug, payload);
        toast.success('Artist updated.');
      } else {
        const created = await adminApi.create(payload);
        toast.success('Artist created.');
        nav(`/admin/artists/${created.slug}/edit`, { replace: true });
        return;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-sm uppercase tracking-[0.25em] text-neutral-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div data-testid="admin-artist-edit-page" className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="w-9 h-9 grid place-items-center border border-neutral-300 hover:border-[#eb5381] hover:text-[#eb5381]">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">{mode === 'edit' ? 'Editing' : 'New Artist'}</div>
            <h1 className="mt-1 font-serif text-3xl md:text-4xl">{mode === 'edit' ? form.name || 'Edit' : 'Add a new artist'}</h1>
          </div>
        </div>
        {mode === 'edit' && form.slug && (
          <a href={`/artists/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] hover:text-[#eb5381]">
            View Live <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      <form onSubmit={submit} className="space-y-10">
        {/* Basics */}
        <Card title="Basics">
          <Grid>
            <Field label="Name *"><Input data-testid="admin-field-name" value={form.name} onChange={e => set('name', e.target.value)} required /></Field>
            <Field label="City *">
              <Select data-testid="admin-field-city" value={form.city} onChange={e => set('city', e.target.value)}>
                {FILTER_CITIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
            </Field>
            <Field label="Slug (URL)"><Input data-testid="admin-field-slug" placeholder="auto-generated if empty" value={form.slug} onChange={e => set('slug', e.target.value)} /></Field>
            <Field label="Instagram handle"><Input value={form.instagram || ''} onChange={e => set('instagram', e.target.value)} placeholder="handle (without @)" /></Field>
            <Field label="Years of Experience"><Input type="number" value={form.years_experience} onChange={e => set('years_experience', e.target.value)} placeholder="e.g. 8" /></Field>
            <Field label="Tier">
              <Select value={form.tier} onChange={e => set('tier', e.target.value)}>
                {FILTER_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </Field>
            <Field label="Product Kit">
              <Select value={form.product_kit} onChange={e => set('product_kit', e.target.value)}>
                {FILTER_PRODUCT_KITS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
            </Field>
            <Field label="Followers"><Input type="number" value={form.followers} onChange={e => set('followers', e.target.value)} /></Field>
            <Field label="Glam Score (0–5)"><Input type="number" step="0.1" value={form.glam_score} onChange={e => set('glam_score', e.target.value)} /></Field>
            <Field label="Starting Price (₹) *"><Input type="number" value={form.starting_price} onChange={e => set('starting_price', e.target.value)} required /></Field>
          </Grid>
          <Field label="Short Bio (2–3 lines)" className="mt-6">
            <Textarea data-testid="admin-field-bio" rows={3} value={form.bio} onChange={e => set('bio', e.target.value)} />
          </Field>
        </Card>

        {/* Flags */}
        <Card title="Flags">
          <div className="flex flex-wrap gap-6">
            <Toggle label="Published (visible on public site)" checked={form.published} onChange={v => set('published', v)} testId="admin-toggle-published" />
            <Toggle label="Verified" checked={form.verified} onChange={v => set('verified', v)} testId="admin-toggle-verified" />
            <Toggle label="Featured (shown on home)" checked={form.featured} onChange={v => set('featured', v)} testId="admin-toggle-featured" />
            <Toggle label="Trial Available" checked={form.trial_available} onChange={v => set('trial_available', v)} />
            <Toggle label="Hairstyling" checked={form.hairstyling} onChange={v => set('hairstyling', v)} />
            <Toggle label="Saree Draping" checked={form.saree_draping} onChange={v => set('saree_draping', v)} />
            <Toggle label="Dupatta Draping" checked={form.dupatta_draping} onChange={v => set('dupatta_draping', v)} />
            <Toggle label="Home Service" checked={form.home_service} onChange={v => set('home_service', v)} />
            <Toggle label="Studio Service" checked={form.studio_service} onChange={v => set('studio_service', v)} />
          </div>
        </Card>

        {/* Categories & Techniques */}
        <Card title="Categories & Techniques">
          <Field label="Occasion / Categories">
            <Pills options={FILTER_OCCASIONS} selected={form.occasions} onToggle={v => toggleIn('occasions', v)} testIdPrefix="admin-occasion" />
          </Field>
          <Field label="Techniques" className="mt-6">
            <Pills options={TECH_OPTS} selected={form.techniques} onToggle={v => toggleIn('techniques', v)} testIdPrefix="admin-technique" />
          </Field>
        </Card>

        {/* Pricing */}
        <Card title="Pricing per Category (leave blank for 'On Request')">
          <Grid cols={5}>
            {['bridal', 'engagement', 'party', 'airbrush', 'sfx'].map(cat => (
              <Field key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                <Input
                  type="number"
                  placeholder="₹"
                  data-testid={`admin-price-${cat}`}
                  value={form.category_prices?.[cat] ?? ''}
                  onChange={e => setPrice(cat, e.target.value)}
                />
              </Field>
            ))}
          </Grid>
        </Card>

        {/* Photos */}
        <Card title="Photos & Videos">
          <p className="text-xs text-neutral-500 mb-5">
            Drag-and-drop directly from your computer — uploaded to ImageKit CDN. Or paste an existing image URL into the field if you prefer.
          </p>

          <Grid>
            <Field label="Profile Image *">
              {form.profile_image && (
                <div className="mb-3 relative inline-block">
                  <img src={ikThumb(form.profile_image, 240)} alt="profile preview" className="w-24 h-24 object-cover border border-[#eaeaea]" />
                  <button type="button" onClick={() => set('profile_image', '')} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-neutral-300 grid place-items-center hover:border-red-500 hover:text-red-500" aria-label="Remove">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <ImageKitUploader
                testId="upload-profile-image"
                accept="image"
                folder={`findyourmua/${slug || 'new'}/profile`}
                label="Upload Profile Image"
                onUploaded={(urls) => set('profile_image', urls[0])}
              />
              <Input data-testid="admin-field-profile-image" value={form.profile_image} onChange={e => set('profile_image', e.target.value)} placeholder="…or paste image URL" className="mt-2" />
            </Field>
            <Field label="Cover Image *">
              {form.cover_image && (
                <div className="mb-3 relative inline-block">
                  <img src={ikThumb(form.cover_image, 320)} alt="cover preview" className="w-40 h-24 object-cover border border-[#eaeaea]" />
                  <button type="button" onClick={() => set('cover_image', '')} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-neutral-300 grid place-items-center hover:border-red-500 hover:text-red-500" aria-label="Remove">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <ImageKitUploader
                testId="upload-cover-image"
                accept="image"
                folder={`findyourmua/${slug || 'new'}/cover`}
                label="Upload Cover Image"
                onUploaded={(urls) => set('cover_image', urls[0])}
              />
              <Input data-testid="admin-field-cover-image" value={form.cover_image} onChange={e => set('cover_image', e.target.value)} placeholder="…or paste image URL" className="mt-2" />
            </Field>
          </Grid>

          <Field label="Portfolio Images" className="mt-6">
            {form.portfolio.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mb-3">
                {form.portfolio.map((src, i) => (
                  <div key={i} className="relative aspect-square">
                    <img src={ikThumb(src, 320)} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover border border-[#eaeaea]" />
                    <button type="button" onClick={() => removeFromList('portfolio', i)} data-testid={`admin-portfolio-remove-${i}`} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-neutral-300 grid place-items-center hover:border-red-500 hover:text-red-500" aria-label="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <ImageKitUploader
              testId="upload-portfolio"
              accept="image"
              multiple
              folder={`findyourmua/${slug || 'new'}/portfolio`}
              label={form.portfolio.length ? 'Add more portfolio images' : 'Upload portfolio images'}
              onUploaded={(urls) => urls.forEach(u => addToList('portfolio', u))}
            />
            <details className="mt-3">
              <summary className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 cursor-pointer hover:text-[#eb5381]">…or paste image URLs</summary>
              <div className="mt-2">
                <UrlList items={form.portfolio} onAdd={(v) => addToList('portfolio', v)} onRemove={(i) => removeFromList('portfolio', i)} testIdPrefix="admin-portfolio" />
              </div>
            </details>
          </Field>

          {form.portfolio.length > 0 && (
            <Field label="Featured Hero Image (shown behind 'Featured' card on home)" className="mt-6">
              <p className="text-xs text-neutral-500 mt-1 mb-3">
                Pick the portfolio image that should appear behind this artist when they are featured on the home page. Defaults to their profile image if nothing is selected.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
                <button
                  type="button"
                  onClick={() => set('featured_image', '')}
                  data-testid="admin-featured-image-default"
                  className={`aspect-square border-2 transition-colors ${
                    !form.featured_image ? 'border-[#eb5381]' : 'border-transparent hover:border-neutral-300'
                  } overflow-hidden relative`}
                  aria-label="Use profile image as featured"
                >
                  {form.profile_image
                    ? <img src={ikThumb(form.profile_image, 240)} alt="" className="w-full h-full object-cover opacity-80" />
                    : <div className="w-full h-full bg-[#f7f7f7]" />}
                  <span className="absolute inset-0 grid place-items-center bg-black/40 text-white text-[9px] uppercase tracking-[0.2em] text-center px-1">
                    Default<br />(profile)
                  </span>
                </button>
                {form.portfolio.map((src, i) => {
                  const active = form.featured_image === src;
                  return (
                    <button
                      type="button"
                      key={i}
                      onClick={() => set('featured_image', src)}
                      data-testid={`admin-featured-image-${i}`}
                      className={`aspect-square border-2 overflow-hidden transition-colors ${
                        active ? 'border-[#eb5381]' : 'border-transparent hover:border-neutral-300'
                      } relative`}
                      aria-label={`Use portfolio image ${i + 1} as featured`}
                    >
                      <img src={ikThumb(src, 240)} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" />
                      {active && (
                        <span className="absolute top-1 right-1 bg-[#eb5381] text-white text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5">
                          Featured
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <Field label="Reels (videos)" className="mt-6">
            {form.reels.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                {form.reels.map((src, i) => (
                  <div key={i} className="relative aspect-[9/16] bg-black">
                    <video src={ikVideo(src, 480, 60)} className="w-full h-full object-cover" muted preload="metadata" />
                    <button type="button" onClick={() => removeFromList('reels', i)} data-testid={`admin-reel-remove-${i}`} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white border border-neutral-300 grid place-items-center hover:border-red-500 hover:text-red-500" aria-label="Remove">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <ImageKitUploader
              testId="upload-reels"
              accept="video"
              multiple
              folder={`findyourmua/${slug || 'new'}/reels`}
              label={form.reels.length ? 'Add more reels' : 'Upload reels (mp4 / mov)'}
              onUploaded={(urls) => urls.forEach(u => addToList('reels', u))}
            />
            <details className="mt-3">
              <summary className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 cursor-pointer hover:text-[#eb5381]">…or paste video URLs</summary>
              <div className="mt-2">
                <UrlList items={form.reels} onAdd={(v) => addToList('reels', v)} onRemove={(i) => removeFromList('reels', i)} testIdPrefix="admin-reel" />
              </div>
            </details>
          </Field>
        </Card>

        {/* Studio */}
        <Card title="Studio Location & Certifications">
          <Field label="Studio Location (text address)"><Input value={form.studio_location || ''} onChange={e => set('studio_location', e.target.value)} placeholder="e.g. Greater Kailash, New Delhi" /></Field>
          <Field label="Certifications" className="mt-6">
            <UrlList items={form.certifications} onAdd={(v) => addToList('certifications', v)} onRemove={(i) => removeFromList('certifications', i)} testIdPrefix="admin-cert" placeholder="e.g. Trained under Meera Sakhrani" />
          </Field>
        </Card>

        {/* Submit */}
        <div className="sticky bottom-0 bg-white border border-[#eaeaea] p-5 flex items-center justify-between gap-4">
          <div className="text-xs text-neutral-500">Changes save instantly on submit.</div>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-save-btn"
            className="inline-flex items-center gap-3 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-60 text-white px-8 py-3 text-[11px] uppercase tracking-[0.25em]"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {mode === 'edit' ? 'Save Changes' : 'Create Artist'}
          </button>
        </div>
      </form>
    </div>
  );
}

const Card = ({ title, children }) => (
  <section className="bg-white border border-[#eaeaea] p-7">
    <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381] mb-5">{title}</div>
    {children}
  </section>
);

const Grid = ({ cols = 2, children }) => (
  <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-x-6 gap-y-5`}>{children}</div>
);

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-1.5">{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#eb5381] outline-none px-0 py-2 text-sm" />
);
const Textarea = (props) => (
  <textarea {...props} className="w-full bg-transparent border border-neutral-200 focus:border-[#eb5381] outline-none p-3 text-sm resize-none" />
);
const Select = (props) => (
  <select {...props} className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#eb5381] outline-none px-0 py-2 text-sm" />
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer text-sm" data-testid={testId}>
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="accent-[#eb5381] w-4 h-4" />
    {label}
  </label>
);

const Pills = ({ options, selected, onToggle, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {options.map(o => {
      const active = selected.includes(o);
      return (
        <button
          type="button"
          key={o}
          onClick={() => onToggle(o)}
          data-testid={`${testIdPrefix}-${o.toLowerCase().replace(/[\s/]+/g, '-')}`}
          className={`text-[11px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${
            active ? 'bg-[#eb5381] text-white border-[#eb5381]' : 'border-neutral-300 hover:border-black'
          }`}
        >
          {o}
        </button>
      );
    })}
  </div>
);

const UrlList = ({ items, onAdd, onRemove, testIdPrefix, placeholder = 'https://…' }) => {
  const [val, setVal] = useState('');
  const add = () => { onAdd(val); setVal(''); };
  return (
    <div>
      <div className="flex gap-2 mt-2">
        <Input data-testid={`${testIdPrefix}-input`} value={val} onChange={e => setVal(e.target.value)} placeholder={placeholder} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" onClick={add} data-testid={`${testIdPrefix}-add-btn`} className="inline-flex items-center gap-1.5 border border-black hover:bg-black hover:text-white px-3 py-2 text-[10px] uppercase tracking-[0.2em]">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {items.map((u, i) => (
            <li key={i} className="flex items-center gap-3 bg-[#fafafa] p-2 text-xs" data-testid={`${testIdPrefix}-item-${i}`}>
              {u.match(/\.(jpe?g|png|webp|avif|gif)/i) ? (
                <img src={u} alt="" className="w-10 h-10 object-cover" />
              ) : (
                <div className="w-10 h-10 grid place-items-center bg-neutral-200 text-[9px] uppercase">URL</div>
              )}
              <a href={u} target="_blank" rel="noreferrer" className="flex-1 truncate hover:text-[#eb5381]">{u}</a>
              <button type="button" onClick={() => onRemove(i)} data-testid={`${testIdPrefix}-remove-${i}`} className="w-7 h-7 grid place-items-center text-neutral-500 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
